import { ArrowUpDown, Check } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/v2/components/ui/popover";
import { cn } from "@/v2/lib/utils";
import type { Entry } from "@/shared/types/entries";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SortMode =
	| "time-desc"
	| "time-asc"
	| "duration-desc"
	| "duration-asc"
	| "matter-asc"
	| "matter-desc";

export type GroupMode = "hour" | "matter" | "application" | "none";

export const DEFAULT_SORT: SortMode = "time-desc";
export const DEFAULT_GROUP: GroupMode = "hour";

export function isSortActive(sort: SortMode, group: GroupMode): boolean {
	return sort !== DEFAULT_SORT || group !== DEFAULT_GROUP;
}

// ---------------------------------------------------------------------------
// Sort + Group logic
// ---------------------------------------------------------------------------

function compareEntries(a: Entry, b: Entry, mode: SortMode): number {
	switch (mode) {
		case "time-desc":
			return (b.end_time || "").localeCompare(a.end_time || "");
		case "time-asc":
			return (a.end_time || "").localeCompare(b.end_time || "");
		case "duration-desc":
			return b.duration - a.duration;
		case "duration-asc":
			return a.duration - b.duration;
		case "matter-asc":
			return (a.matter || "").localeCompare(b.matter || "");
		case "matter-desc":
			return (b.matter || "").localeCompare(a.matter || "");
	}
}

/** Parse entry.date ("MM/dd/yyyy") into a Date */
import { parseEntryDate } from "@/v2/lib/dates";

function formatRelativeDate(d: Date): string {
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
	const diffDays = Math.round((today.getTime() - target.getTime()) / 86400000);

	if (diffDays === 0) return "Today";
	if (diffDays === 1) return "Yesterday";
	if (diffDays === -1) return "Tomorrow";

	const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
	const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
	const dayName = days[target.getDay()];
	const month = months[target.getMonth()];
	const day = target.getDate();

	if (diffDays > 1 && diffDays < 7) return `${dayName}, ${month} ${day}`;
	if (target.getFullYear() !== now.getFullYear()) return `${month} ${day}, ${target.getFullYear()}`;
	return `${dayName}, ${month} ${day}`;
}

function getGroupKey(entry: Entry, mode: GroupMode, multiDate?: boolean): string {
	switch (mode) {
		case "hour": {
			if (!entry.end_time) return "Unknown";
			const d = new Date(entry.end_time);
			const hours = d.getUTCHours();
			const period = hours >= 12 ? "PM" : "AM";
			const displayHour = hours % 12 || 12;
			const hourLabel = `${displayHour}:00 ${period}`;
			if (multiDate && entry.date) {
				const entryDate = parseEntryDate(entry.date);
				return `${formatRelativeDate(entryDate)} \u00b7 ${hourLabel}`;
			}
			return hourLabel;
		}
		case "matter":
			return entry.matter || "No matter";
		case "application":
			return entry.application || "Unknown";
		case "none":
			return "";
	}
}

export function sortAndGroup(
	entries: Entry[],
	sort: SortMode,
	group: GroupMode,
	multiDate?: boolean,
): Record<string, Entry[]> {
	const sorted = [...entries].sort((a, b) => compareEntries(a, b, sort));

	if (group === "none") {
		return { "": sorted };
	}

	const groups: Record<string, Entry[]> = {};
	for (const entry of sorted) {
		const key = getGroupKey(entry, group, multiDate);
		if (!groups[key]) groups[key] = [];
		groups[key].push(entry);
	}
	return groups;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SortPopoverProps {
	sort: SortMode;
	group: GroupMode;
	onSortChange: (sort: SortMode) => void;
	onGroupChange: (group: GroupMode) => void;
}

const sortOptions: { value: SortMode; label: string }[] = [
	{ value: "time-desc", label: "Newest first" },
	{ value: "time-asc", label: "Oldest first" },
	{ value: "duration-desc", label: "Duration (high to low)" },
	{ value: "duration-asc", label: "Duration (low to high)" },
	{ value: "matter-asc", label: "Matter (A to Z)" },
	{ value: "matter-desc", label: "Matter (Z to A)" },
];

const groupOptions: { value: GroupMode; label: string }[] = [
	{ value: "hour", label: "Hour" },
	{ value: "matter", label: "Matter" },
	{ value: "application", label: "Application" },
];

export function SortPopover({ sort, group, onSortChange, onGroupChange }: SortPopoverProps) {
	const active = isSortActive(sort, group);

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative h-10 w-10 text-muted-foreground"
				>
					<ArrowUpDown className="size-5" />
					{active && (
						<span className="absolute top-1 right-1 size-1.5 rounded-full bg-primary" />
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-52 p-0" align="end">
				{/* Sort section */}
				<div className="px-3 pt-3 pb-1.5">
					<span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Sort by</span>
				</div>
				<div className="px-1.5 pb-1.5">
					{sortOptions.map(({ value, label }) => (
						<button
							key={value}
							type="button"
							onClick={() => onSortChange(value)}
							className={cn(
								"flex items-center justify-between w-full px-2 py-1.5 rounded-md text-sm text-left transition-colors",
								sort === value
									? "bg-accent text-foreground"
									: "text-foreground/80 hover:bg-accent",
							)}
						>
							{label}
							{sort === value && <Check className="size-3.5 text-primary shrink-0" />}
						</button>
					))}
				</div>

				{/* Group section */}
				<div className="border-t border-border">
					<div className="px-3 pt-2.5 pb-1.5">
						<span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Group by</span>
					</div>
					<div className="px-1.5 pb-1.5">
						{groupOptions.map(({ value, label }) => (
							<button
								key={value}
								type="button"
								onClick={() => onGroupChange(value)}
								className={cn(
									"flex items-center justify-between w-full px-2 py-1.5 rounded-md text-sm text-left transition-colors",
									group === value
										? "bg-accent text-foreground"
										: "text-foreground/80 hover:bg-accent",
								)}
							>
								{label}
								{group === value && <Check className="size-3.5 text-primary shrink-0" />}
							</button>
						))}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}
